import { graphql, navigate, useStaticQuery } from "gatsby";
import { Box, Flex, Spinner } from "@theme-ui/components";
import ReCAPTCHA from "react-google-recaptcha";
import React, { useEffect, useRef, useState } from "react";
import queryString from "query-string";
import PropTypes from "prop-types";
import _ from "lodash";
import { validate, subscribeV1 } from "../hubspot/hsAPI";
import { Button, InputBox } from "../atoms";
import { ActionBanner } from "../molecules";
import "../../utils/glassToken.css";
import { colors, typography } from "../../styles/theme";

let recaptchaRef = React.createRef();

const SERVER_ERROR =
  "Sorry, some error seems to have occurred and we could not complete your request. Please try again.";

const NewsletterForm = ({
  pageUrl,
  pageCode,
  pageName,
  formName,
  setHideForm,
  setIsSuccess,
  search,
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState({});
  const [processing, setProcessing] = useState(false);
  const searchParams = queryString.parse(search);
  const utm_source = searchParams["utm_source"];
  const utm_medium = searchParams["utm_medium"];
  const utm_campaign = searchParams["utm_campaign"];

  const validateForm = () => {
    let error = {};
    if (email) {
      const emailErr = validate(email);
      emailErr && (error["email"] = emailErr);
    } else {
      error["email"] = "Please fill in this field.";
    }
    if (!name) {
      error["name"] = "Please fill in this field.";
    }
    return error;
  };

  const handleClick = async (e) => {
    e.preventDefault();
    setError({});
    setProcessing(true);
    const formErrors = validateForm();
    if (!_.isEmpty(formErrors)) {
      setError(formErrors);
      setProcessing(false);
      return;
    }

    if (recaptchaRef && recaptchaRef.current) {
      let captcha = "";
      recaptchaRef.current.reset();
      captcha = await recaptchaRef.current.executeAsync();
      const formData = {
        email,
        full_name: name,
        form_name: formName,
        page_code: pageCode,
        page_name: pageName,
        page_url: pageUrl,
        utm_source,
        utm_medium,
        utm_campaign,
        captcha,
        invisible_recaptcha: true,
      };

      if (captcha) {
        subscribeV1(
          formData,
          (response) => {
            setProcessing(false);
            setHideForm(true);
            setIsSuccess(true);
          },
          (error) => {
            setProcessing(false);
            if (
              error.response &&
              error.response.status == 400 &&
              error.response.data.invalid_email
            ) {
              setError((prev) => ({
                ...prev,
                email: error.response.data.invalid_email,
              }));
            } else {
              setHideForm(true);
              setIsSuccess(false);
            }
          }
        );
      } else {
        setProcessing(false);
        setHideForm(true);
        setIsSuccess(false);
      }
    } else {
      setProcessing(false);
      setHideForm(true);
      setIsSuccess(false);
    }
  };

  return (
    <Flex
      className="glass50"
      sx={styles.container}
      as="form"
      onSubmit={handleClick}
    >
      <Flex sx={styles.subContainer}>
        <InputBox
          label="Full Name"
          placeholder="Type here.."
          labelColor={colors.white}
          errorColor={colors.white}
          isRequired={true}
          name="fullname"
          value={name}
          maxlength={100}
          error={error?.name}
          customStyles={{ width: "100%" }}
          onChange={(e) => setName(e.target.value)}
        />
        <InputBox
          label="Business Email"
          placeholder="Type here.."
          isRequired={true}
          labelColor={colors.white}
          errorColor={colors.white}
          name="email"
          value={email}
          maxlength={100}
          customStyles={{ width: "100%" }}
          error={error?.email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Flex>
      {processing ? (
        <Spinner color="white" size={58} />
      ) : (
        <Button variant="outlined" size="large" disabled={!email || !name}>
          Submit
        </Button>
      )}
    </Flex>
  );
};

const styles = {
  container: {
    borderRadius: "16px",
    p: ["1.5rem", "1.5rem", "1.5rem", "2.5rem"],
    border: "1px solid rgba(232, 232, 233, 0.50)",
    width: "100%",
    maxWidth: ["21.813rem", "21.813rem", "44rem", "66.875rem"],
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  subContainer: {
    justifyContent: "space-around",
    flexDirection: ["column", "column", "row", "row"],
    width: "100%",
    gap: "1.5rem",
    px: [0, 0, 0, "2.5rem"],
    mb: "3rem",
  },
};

const NewsletterFormSection = ({
  pageUrl,
  formName,
  pageCode,
  pageName,
  search,
}) => {
  const [hideForm, setHideForm] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { greytalk } = useStaticQuery(graphql`
    query {
      greytalk: file(relativePath: { eq: "resources/greytalk-logo.png" }) {
        childImageSharp {
          gatsbyImageData(width: 200, layout: CONSTRAINED)
        }
      }
    }
  `);

  const refresh = () => {
    setHideForm(false);
    setIsSuccess(false);
    navigate("#");
  };

  const newsletterRef = useRef(null);

  useEffect(() => {
    const scrollToNewsletter = () => {
      if (window.location.hash === "#newsletter" && newsletterRef.current) {
        newsletterRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    };

    const scrollTimeout = setTimeout(scrollToNewsletter, 100);

    return () => {
      clearTimeout(scrollTimeout);
    };
  }, []);

  return (
    <Box
      sx={{
        width: "80%",
        margin: "auto",
      }}
    >
      <Box as="h2" sx={{ ...typography.h2, textAlign: "center", mb: "1rem" }}>
        Subscribe to our newsletter
      </Box>
      <iframe
        src="https://campaign.greythrconnect.com/l/1091962/2025-03-04/3n6w6z"
        width="100%"
        height="300"
        type="text/html"
        frameborder="0"
        allowTransparency="true"
        style={{
          border: `1px solid ${colors.neutral500}`,
          borderRadius: "1rem",
        }}
      ></iframe>
    </Box>
  );
};

NewsletterForm.propTypes = {
  pageUrl: PropTypes.string,
  formName: PropTypes.string,
  setHideForm: PropTypes.func,
  setIsSuccess: PropTypes.func,
  pageCode: PropTypes.string,
  pageName: PropTypes.string,
  search: PropTypes.string,
};

NewsletterFormSection.propTypes = {
  pageUrl: PropTypes.string,
  formName: PropTypes.string,
  pageCode: PropTypes.string,
  pageName: PropTypes.string,
  search: PropTypes.string,
};

export default NewsletterFormSection;
